import { ACTION_OPEN_SEARCH_OR_RESERVE_MODAL } from 'utils/analytics/eventTypeConstantsAmplitude';
import { Dispatch, SetStateAction, useState } from 'react';
import { IconSearch } from '@tock/icons';
import { StoreType, useActions } from '@tock/shared/utils/connect';
import { Theme, css } from '@emotion/react';
import { Typography } from 'omakase';
import { allMetroAreasSelector } from 'selectors/appSelector';
import { consumer } from '@tock/coretypes';
import { currentMetroOrUndefinedSelector } from 'selectors/metroAreaSelectors';
import { searchBarSuggestionsSectionedSelector } from 'selectors/searchBarSuggestionsSelector';
import { useAmplitudeSendEvent } from 'utils/analytics/AnalyticsContext';
import { useSelector } from 'react-redux';
import SearchLauncherWidget from 'consumer-page-widgets/SearchLauncherWidget';
import SearchNav from 'components/nav-search/SearchNav';

interface SearchTubeHeroProps {
  metroArea?: consumer.IMetroArea;
}

interface SearchTubeMobileProps extends SearchTubeHeroProps {
  setSearchOrReservationOpen: Dispatch<SetStateAction<boolean>>;
}

const SearchTubeHero: React.FC<SearchTubeHeroProps> = ({ metroArea }) => {
  const [searchOrReservationOpen, setSearchOrReservationOpen] = useState(false);
  const allMetroAreas = useSelector(allMetroAreasSelector);
  const currentMetroArea = useSelector(currentMetroOrUndefinedSelector);
  const currentSearch = useSelector((state: StoreType) => state.search.currentSearch);
  const sectionedSearchBarSuggestions = useSelector(searchBarSuggestionsSectionedSelector);

  const {
    actions: {
      discover: { autocompleteBusinessSuggestions },
      search: { clearSearchResults, setCurrentSearch },
      metroArea: { selectMetroArea },
    },
  } = useActions();

  const handleBusinessAutoComplete = (value: string) => {
    let coordinates = undefined;
    if (currentMetroArea?.lat && currentMetroArea?.lng) {
      coordinates = { lat: currentMetroArea.lat, lng: currentMetroArea.lng };
    }
    return autocompleteBusinessSuggestions(value, coordinates);
  };

  return (
    <div
      css={(theme: Theme) => css`
        margin: 20px 0 -40px;
        ${theme.breakpoints.up('md')} {
          margin: 50px 0 0;
        }
      `}
    >
      <SearchTubeDesktop metroArea={metroArea} />
      <SearchTubeMobile
        metroArea={metroArea}
        setSearchOrReservationOpen={setSearchOrReservationOpen}
      />
      <SearchNav
        allMetroAreas={allMetroAreas}
        onSearch={handleBusinessAutoComplete}
        clearSearchResults={clearSearchResults}
        currentMetroArea={currentMetroArea}
        currentSearch={currentSearch}
        onSelectMetroArea={(metroArea) => {
          selectMetroArea(metroArea);
          window.analytics.sendEvent('MetroArea', 'selected from global nav', `${metroArea.name}`);
        }}
        searchResults={sectionedSearchBarSuggestions}
        setCurrentSearch={setCurrentSearch}
        isDialogOpen={searchOrReservationOpen}
        onClose={() => setSearchOrReservationOpen(false)}
        metroArea={metroArea}
      />
    </div>
  );
};

const SearchTubeDesktop: React.FC<SearchTubeHeroProps> = ({ metroArea }) => {
  return (
    <div
      css={(theme: Theme) => css`
        ${theme.breakpoints.down('sm')} {
          display: none;
        }
        .search-launcher-container {
          width: calc(100% - 120px);
        }
      `}
    >
      <SearchLauncherWidget metroArea={metroArea} fullWidth />
    </div>
  );
};

const SearchTubeMobile: React.FC<SearchTubeMobileProps> = ({
  metroArea,
  setSearchOrReservationOpen,
}) => {
  const amplitudeSendEvent = useAmplitudeSendEvent();
  return (
    <div
      css={(theme: Theme) => css`
        padding: 0 20px;
        position: relative;
        z-index: 1;
        ${theme.breakpoints.up('md')} {
          display: none;
        }
      `}
    >
      <button
        onClick={() => {
          setSearchOrReservationOpen((val) => !val);
          amplitudeSendEvent(ACTION_OPEN_SEARCH_OR_RESERVE_MODAL);
        }}
        css={(theme: Theme) => css`
          border: 1px solid ${theme.colors.borderDefault};
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 100%;
          text-align: left;
          border-radius: 44px;
          padding: 1rem 1.2rem 1rem 2.6rem;
          background-color: ${theme.colors.bgDefault};
        `}
        data-testid="search-for-city-button"
        aria-label="Open search"
      >
        <div>
          <Typography color="fgPrimaryBase" variant="body01Strong">
            Delicious starts here.
          </Typography>
          <Typography variant="caption">{metroArea?.name}</Typography>
        </div>
        <div
          css={({ colors, borderRadius }: Theme) => css`
            background-color: ${colors.fgPrimaryBase};
            color: #fff;
            padding: 1rem;
            border-radius: ${borderRadius.Pill};
            aspect-ratio: 1/1;
            min-height: 0;
          `}
        >
          <IconSearch
            css={css`
              font-size: 17px;
            `}
          />
        </div>
      </button>
    </div>
  );
};

export default SearchTubeHero;
